<template>
    <div class="invoice__preview--wrap">
        <div class="w-100">
            <div class="invoice__preview--contents">
                <div class="invoice__preview__top" />
                <div class="pl--15 pr--16">
                    <div class="invoice__preview--header preview__header">
                        <div>
                            <h1>
                                Invoice
                            </h1>
                            <p class="text--sm">
                                Invoice: <span>#{{ invoiceData.invoice.invoiceNo }}</span>
                            </p>
                        </div>
                        <div class="">
                            <CustomAvatar
                                border-style="solid" 
                                width="100px" 
                                type="square"
                                :initials="$initials(user.fullName || '')" 
                                :src="profileUrlValue"
                                class="text--lg"
                            />
                        </div>
                    </div>
                </div>
                <div class="invoice__preview--card">
                    <div class="preview__dates">
                        <div class="flex-1">
                            <p class="text--sm text--semi--bold">
                                Date Issued
                            </p>
                            <p class="text--sm">
                                {{ $format_date(invoiceData.invoice.createdAt) || '-' }}
                            </p>
                        </div>
                        <div class="flex-1">
                            <p class="text--sm text--semi--bold">
                                Due Date
                            </p>
                            <p class="text--sm">
                                {{ $format_date(invoiceData.invoice.dueDate) || '-' }}
                            </p>
                        </div>
                    </div>

                    <div class="preview__details">
                        <div class="preview__details__item">
                            <CustomText size="base" transform="upper" weight="semi--bold">
                                From:
                            </CustomText>
                            <div class="preview__card__item">
                                <p class="preview__card__item__title">
                                    {{ user.fullName }}
                                </p>
                                <p class="preview__card__item__address">
                                    {{ user.address }}
                                </p>
                                <p>{{ user.phoneNumber || '-' }}</p>
                                <p>{{ user.email || '-' }}</p>
                            </div>
                        </div>
                        <div class="preview__details__item mr--10">
                            <CustomText size="base" transform="upper" weight="semi--bold">
                                Billed to:
                            </CustomText>
                            <div v-if="invoiceData.client.name" class="preview__card__item">
                                <p class="preview__card__item__title">
                                    {{ invoiceData.client.name }}
                                </p>
                                <p>{{ invoiceData.client.email }}</p>
                                <p>{{ invoiceData.client.phoneNumber || '-' }}</p>
                                <p>{{ invoiceData.client.address || '-' }}</p>
                                <p>{{ invoiceData.client.country || '-' }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="mt--40 mb--10 text--xl text--medium">
                        {{ invoiceData.invoice.items.length }} {{ computeItemsLengthText }}
                    </div>

                    <table class="table table-light mt--20">
                        <thead>
                            <tr>
                                <th scope="col">
                                    Description
                                </th>
                                <th scope="col">
                                    Quantity
                                </th>
                                <th scope="col">
                                    Price
                                </th>
                                <th scope="col">
                                    Amount
                                </th>
                            </tr>
                        </thead>
                        <tbody v-for="(item, index) in invoiceData.invoice.items" :key="index">
                            <tr>
                                <td>{{ item.itemName || "-" }}</td>
                                <td>{{ item.itemQuantity || 0 }}</td>
                                <td>{{ $country.getSymbol(invoiceData.invoice.currency, 'currency') || '-' }}{{ $format_amount(item.itemPrice, 2)|| 0 }}</td>
                                <td>{{ $country.getSymbol(invoiceData.invoice.currency, 'currency') || '-' }}{{ $format_amount(item.itemUnit, 2) || 0 }}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="mt--30">
                        <div class="preview__row">
                            <div class="text--sm">
                                Subtotal:
                            </div>
                            <div class="preview__row__text">
                                {{ $country.getSymbol(invoiceData.invoice.currency, 'currency') || '-' }}{{ invoiceData.invoice.subtotal || 0 }}
                            </div>
                        </div>
                        <div class="preview__row">
                            <div class="text--sm">
                                Tax:
                            </div>
                            <div class="preview__row__text">
                                {{ $country.getSymbol(invoiceData.invoice.currency, 'currency') || '-' }}{{ invoiceData.invoice.tax }}
                            </div>
                        </div>
                        <div class="preview__row">
                            <div class="text--sm">
                                Total:
                            </div>
                            <div class="preview__row__text">
                                {{ $country.getSymbol(invoiceData.invoice.currency, 'currency') || 'N/A' }}{{ invoiceData.invoice.total || 0 }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { formatDateStrings } from "@/utils/date";
import CustomAvatar from "../../ui/avatar/CustomAvatar.vue";
import CustomText from "../../ui/text/Text.vue";

export default {
    name: "PreviewInvoice",
    
    components: {
        CustomText,
        CustomAvatar,
    },

    props: {
        invoiceData: {
            type: Object,
            default: undefined,
        },
    },

    computed: {
        computeItemsLengthText() {
            if (this.invoiceData.invoice.items.length > 1 || !this.invoiceData.invoice.items.length) {
                return "items";
            } else {
                return "item";
            }
        },

        user() {
            return this.$store.getters["auth/profile"];
        },

        profileUrlValue() {
            return this.user.profileImageUrl || "";
        },
    },
    
    methods: {
        formatDate(dateString) {
            if (typeof dateString === "object") {
                let stringDate = dateString.toString();
                return formatDateStrings(stringDate) || "N/A";
            }
            return dateString && typeof dateString === "string" ? formatDateStrings(dateString) : "N/A";
        },
    },
};
</script>

<style lang="scss" scoped>
    thead {
        border-style: solid !important;
    }

    th {
        padding-left: 0px !important;
    }

    .table tbody td {
        overflow-x: auto !important;
        white-space: normal !important;
        text-overflow: unset !important;
    }

    .preview {
        &__header {
            border-bottom: 1px solid #eee;
            padding-bottom: 15px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            // margin-top: 1rem;
        }

        &__dates {
            display: flex;
            align-items: center;
            margin-bottom: 20px;
            border-bottom: 1px solid #eee;
            padding-bottom: 10px;
        }

        &__card {
            padding-top: 1.3rem;
        }

        &__row {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            width: 100%;
            justify-content: flex-end;

            &__text {
                font-size: 0.875rem;
                font-weight: 600;
                text-transform: uppercase;
                margin-left: 10px;
            }
        }

        &__card__item {
            &__title {
                font-weight: 500;
                margin-bottom: 3px !important;
                margin-top: 10px;
            }

            p {
                font-size: 0.875rem !important;    
            }
        }

        &__details {
            display: flex;
            align-items: baseline;

            &__item {
                flex: 1;
            }
        }

        &__logo {
            border: 1px solid #e6e6e6;
            padding: 10px;
            border-radius: 5px;
        }
    }
</style>